<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="activation"/>
        <div class="pi-act__head">
          <div>
            <h2 class="pi-act__title">Ativação das empresas vendidas</h2>
            <p class="pi-section__hint">D0 = data da venda · marcos contados até D+15, D+30 e D+60</p>
          </div>
          <div class="pi-act__actions">
            <p v-if="headerText" class="pi-act__updated">{{ headerText }}</p>
            <pi-export-screen screen="Ativação" :filters="screenExportFilters" :period="exportPeriodValue"/>
          </div>
        </div>
        <pi-onboarding-filters v-model="filters"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <section v-if="companySelected && !blocked" class="pi-section" aria-labelledby="pi-act-company">
          <div class="pi-section__head">
            <h2 id="pi-act-company" class="pi-section__title">Empresa: {{ companyName }}</h2>
            <router-link v-if="companyFicha" :to="companyFicha" class="pi-link">Abrir a ficha da empresa</router-link>
          </div>
          <p class="pi-notice">Os blocos abaixo continuam com todas as empresas do recorte; o filtro de empresa vale só para este checklist.</p>
          <pi-data-state :loading="company.loading" :error="company.error" :envelope="companyEnvelope" :empty="companyEmpty"
                         empty-message="Empresa fora do acompanhamento de ativação (interna, sem venda no horizonte de 400 dias ou ainda não lida)."
                         :show-footer="false" @retry="loadCompany(true)">
            <pi-onboarding-company :view="companyView" :definition-version="companyVersion"/>
          </pi-data-state>
        </section>

        <pi-data-state :loading="summary.loading" :error="summary.error" :envelope="summaryEnvelope" :empty="summaryEmpty"
                       empty-message="Nenhuma empresa no recorte de vendas." :show-footer="false" @retry="loadSummary(true)">
          <section class="pi-section" aria-labelledby="pi-act-cards">
            <div class="pi-section__head">
              <h2 id="pi-act-cards" class="pi-section__title">Resultado até a janela</h2>
              <div class="pi-act__choice">
                <div class="pi-toggle" role="group" aria-label="Janela dos cartões">
                  <button v-for="days in windows" :key="days" type="button" class="pi-toggle__btn" :class="{ 'is-on': choice.window === days }"
                          :aria-pressed="choice.window === days ? 'true' : 'false'" @click="chooseWindow(days)">D{{ days }}</button>
                </div>
                <pi-definition-tooltip info-key="activation.cardWindow" label="Janela dos cartões"/>
              </div>
              <p class="pi-section__hint">{{ rangeText }}</p>
            </div>
            <div class="pi-cards">
              <pi-kpi-card v-for="card in cards" :key="card.key" :label="card.label" :metric="card.metric" :definition-key="card.definitionKey"
                           :info-key="card.infoKey" :definition-version="summaryVersion" :compare="compare">
                <p v-for="note in card.notes" :key="note" class="pi-act__card-note">{{ note }}</p>
                <p class="pi-act__card-hint">{{ card.hint }}</p>
              </pi-kpi-card>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-act-matrix">
            <div class="pi-section__head">
              <h2 id="pi-act-matrix" class="pi-section__title">Evolução D15 → D30 → D60</h2>
              <p class="pi-section__hint">Clique numa célula para ver as empresas que não chegaram ao marco.</p>
              <pi-export-menu title="Evolução D15 → D30 → D60" screen="Ativação" :columns="matrixColumns" :rows="matrixTable" :envelope="summary.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!summary.loaded || !!summary.error"/>
            </div>
            <pi-onboarding-matrix :groups="matrix" :quantities="quantities" :notes="censusNotes" :selected="matrixSelected" @select="openMilestone"/>
            <p class="pi-muted pi-act__note">{{ rulesNote }} {{ defaultsNote }}</p>
          </section>

          <section class="pi-section" aria-labelledby="pi-act-stages">
            <div class="pi-section__head">
              <h2 id="pi-act-stages" class="pi-section__title">Onde as empresas param</h2>
              <pi-definition-tooltip info-key="activation.stageWindow" label="Etapa até a janela"/>
              <p class="pi-section__hint">Etapa mais avançada atingida até cada janela. Clique num segmento para ver a lista.</p>
              <pi-export-menu title="Onde as empresas param" screen="Ativação" :columns="stageColumns" :rows="stageTable" :envelope="summary.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!summary.loaded || !!summary.error"/>
            </div>
            <pi-stage-bars :rows="stageBars" :selected="stageSelected" @select="openStage"/>
          </section>

          <section class="pi-section" aria-labelledby="pi-act-bottlenecks">
            <div class="pi-section__head">
              <h2 id="pi-act-bottlenecks" class="pi-section__title">Gargalos</h2>
              <pi-definition-tooltip info-key="activation.bottleneck" label="Gargalos"/>
              <p class="pi-section__hint">Conversão entre etapas com base de pelo menos 20 empresas; as duas piores em D30 e D60.</p>
              <pi-export-menu title="Gargalos" screen="Ativação" :columns="bottleneckColumns" :rows="bottlenecks" :envelope="summary.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="!summary.loaded || !!summary.error || !bottlenecks.length"/>
            </div>
            <ul v-if="bottlenecks.length" class="pi-act__bottlenecks">
              <li v-for="item in bottlenecks" :key="item.key">
                {{ item.text }}
                <span v-if="item.baseText" class="pi-table__sub">{{ item.baseText }}</span>
              </li>
            </ul>
            <p v-else class="pi-muted">Nenhuma conversão com base suficiente (20 empresas) no recorte.</p>
            <div class="row">
              <div class="col-xs-12 col-md-7">
                <div class="pi-section__head">
                  <h3 class="pi-subtitle">Tempo até o marco</h3>
                  <pi-export-menu title="Tempo até o marco" screen="Ativação" :columns="timeColumns" :rows="timeToMilestone" :envelope="summary.envelope"
                                  :filters="exportFilters" :period="exportPeriodValue" :disabled="!summary.loaded || !!summary.error || !timeToMilestone.length"/>
                </div>
                <div class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Dias desde a venda até cada marco, entre as empresas com D60 fechado que atingiram o marco até D60</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in timeHeaderColumns" :key="column.key" :column="column"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in timeToMilestone" :key="row.key">
                      <th scope="row" class="pi-act__row-name">{{ row.label }}</th>
                      <td class="is-num">{{ row.reachedText }}</td>
                      <td class="is-num">{{ row.medianText }}</td>
                      <td class="is-num">{{ row.p75Text }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p class="pi-muted pi-act__note">Empresas com D60 fechado que atingiram o marco até D60; marco antes da venda conta como D+0.</p>
              </div>
              <div class="col-xs-12 col-md-5">
                <div class="pi-section__head">
                  <h3 class="pi-subtitle">Dias até o primeiro valor</h3>
                  <pi-definition-tooltip info-key="activation.timeToValue" label="Dias até o primeiro valor"/>
                  <pi-export-menu title="Dias até o primeiro valor" screen="Ativação" :columns="ttvColumns" :rows="ttvHistogram" :envelope="summary.envelope"
                                  :filters="exportFilters" :period="exportPeriodValue" :disabled="!summary.loaded || !!summary.error"/>
                </div>
                <pi-bar-list :rows="ttvHistogram" empty-text="Nenhuma empresa com D60 fechado no recorte."/>
              </div>
            </div>
          </section>
        </pi-data-state>

        <section v-if="drill && !blocked" ref="drill" class="pi-section" aria-labelledby="pi-act-drill">
          <div class="pi-section__head">
            <h2 id="pi-act-drill" ref="drillTitle" class="pi-section__title" tabindex="-1">{{ drillTitleText }}</h2>
            <button v-if="drill.kind === 'milestone'" type="button" class="pi-btn pi-btn--ghost" @click="toggleReached">{{ drillToggle }}</button>
            <pi-export-menu :title="drillTitleText" screen="Ativação" :columns="drillColumns" :source="drillSource" :filters="drillExportFilters"
                            :period="exportPeriodValue" :disabled="!drillCompanies.loaded || !!drillCompanies.error || !drillTotal"/>
            <button type="button" class="pi-btn pi-btn--ghost" @click="closeDrill">Fechar</button>
          </div>
          <pi-data-state :loading="drillCompanies.loading" :error="drillCompanies.error" :envelope="drillEnvelope"
                         :empty="drillCompanies.loaded && !drillRows.length" empty-message="Nenhuma empresa nesta célula."
                         :show-footer="false" @retry="loadDrill(true)">
            <pi-onboarding-company-table :rows="drillRows" :columns="drillColumns" :sort-state="drillSort" :total="drillTotal" :page="drillPage"
                                         :page-size="drillPageSize" :query="$route.query" :loading="drillCompanies.loading" caption="Empresas da célula escolhida"
                                         @sort="sortDrill" @update:page="changeDrillPage" @update:pageSize="changeDrillPageSize"/>
          </pi-data-state>
        </section>

        <section v-if="!blocked" class="pi-section" aria-labelledby="pi-act-cohorts">
          <div class="pi-section__head">
            <h2 id="pi-act-cohorts" class="pi-section__title">{{ cohortTitle }}</h2>
            <div class="pi-act__choice">
              <div class="pi-toggle" role="group" aria-label="Indicador das coortes">
                <button v-for="item in indicators" :key="item.key" type="button" class="pi-toggle__btn" :class="{ 'is-on': choice.indicator === item.key }"
                        :aria-pressed="choice.indicator === item.key ? 'true' : 'false'" @click="chooseIndicator(item.key)">{{ item.label }}</button>
              </div>
              <pi-definition-tooltip info-key="activation.cohortIndicator" label="Indicador das coortes"/>
            </div>
            <pi-export-menu :title="cohortTitle" screen="Ativação" :columns="cohortTableColumns" :rows="cohortTable" :envelope="cohorts.envelope"
                            :filters="cohortExportFilters" :period="exportPeriodValue" :disabled="!cohorts.loaded || !!cohorts.error || !cohortTable.length"/>
          </div>
          <pi-data-state :loading="cohorts.loading" :error="cohorts.error" :envelope="cohortsEnvelope" :empty="cohorts.loaded && !cohortRows.length"
                         empty-message="Nenhuma coorte no período." :show-footer="false" @retry="loadCohorts(true)">
            <pi-line-chart :title="indicatorText + ' por coorte (D15, D30 e D60)'" :series="cohortSeries" unit="pct" :format-label="cohortFormatter"
                           partial-label="Ponto vazado = coorte com a janela aberta ou aguardando medição" :height="220"
                           partial-flag="Janela aberta ou aguardando medição para parte da coorte: o valor ainda pode mudar"/>
            <div class="pi-table-wrap">
              <table class="pi-table">
                <caption class="pi-sr">{{ indicatorText }} por coorte de venda em D15, D30 e D60</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in cohortHeaderColumns" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in cohortRowsRecentFirst" :key="row.key">
                  <th scope="row" class="pi-act__row-name">
                    {{ row.label }}
                    <span v-if="row.smallSample" class="pi-badge pi-badge--muted" title="Menos de 20 empresas na coorte">◦ amostra pequena</span>
                  </th>
                  <td class="is-num">
                    {{ row.countText }}
                    <span v-if="row.countSub" class="pi-table__sub">{{ row.countSub }}</span>
                  </td>
                  <td v-for="cell in row.cells" :key="row.key + '-' + cell.key" class="is-num" :class="{ 'pi-act__open': cell.open }" :title="cell.title || null">
                    <strong>{{ cell.text }}</strong>
                    <span v-if="cell.sub" class="pi-table__sub">{{ cell.sub }}</span>
                  </td>
                  <td class="is-num">{{ row.sourceBText }}</td>
                  <td class="is-num" :title="row.medianPunchTitle || null">{{ row.medianPunchText }}</td>
                  <td class="is-num">{{ row.medianEmployeesText }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </pi-data-state>
        </section>

        <section v-if="!blocked" class="pi-section" aria-labelledby="pi-act-stuck">
          <div class="pi-section__head">
            <h2 id="pi-act-stuck" class="pi-section__title">Empresas travadas</h2>
            <p class="pi-section__hint">Vendidas, não canceladas nem excluídas, até D+75. Um motivo por empresa: o primeiro do trilho que se aplica.</p>
            <pi-export-menu title="Empresas travadas" screen="Ativação" :columns="stuckColumns" :source="stuckSource" :filters="stuckExportFilters"
                            :disabled="!stuck.loaded || !!stuck.error || !stuckTotal"/>
          </div>
          <div class="pi-toolbar">
            <filter-select label="Motivo" :value="stuckReason" :options="reasonOptions" all-label="Todos" @input="setStuckReason">
              <pi-definition-tooltip slot="info" class="pi-act__info" info-key="activation.stuckReason" label="Motivo"/>
            </filter-select>
            <filter-select label="Etapa" :value="stuckStage" :options="stageOptions" all-label="Todas" @input="setStuckStage">
              <pi-definition-tooltip slot="info" class="pi-act__info" info-key="activation.stageNow" label="Etapa"/>
            </filter-select>
          </div>
          <p v-if="stuckIgnored" class="pi-notice">{{ stuckIgnored }}</p>
          <pi-data-state :loading="stuck.loading" :error="stuck.error" :envelope="stuckEnvelope" :empty="stuck.loaded && !stuckRows.length"
                         empty-message="Nenhuma empresa travada com esses filtros." :show-footer="false" @retry="loadStuck(true)">
            <ul v-if="counters.length" class="pi-act__counters" aria-label="Empresas travadas por motivo">
              <li v-for="counter in counters" :key="counter.key">
                <button type="button" class="pi-btn pi-btn--ghost" :aria-pressed="stuckReason === counter.key ? 'true' : 'false'"
                        :class="{ 'is-on': stuckReason === counter.key }" @click="toggleReason(counter.key)">{{ counter.text }}</button>
              </li>
            </ul>
            <pi-onboarding-company-table :rows="stuckRows" :columns="stuckColumns" :sort-state="stuckSort" :total="stuckTotal" :page="stuckPage"
                                         :page-size="stuckPageSize" :query="$route.query" :loading="stuck.loading" caption="Empresas travadas na ativação"
                                         @sort="sortStuck" @update:page="changeStuckPage" @update:pageSize="changeStuckPageSize"/>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[summary.envelope, cohorts.envelope, stuck.envelope, drillCompanies.envelope, company.envelope]"
                        :labels="['Resumo', 'Coortes', 'Travadas', 'Lista', 'Empresa']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import filterSelect from "../../components/default/filterSelect.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piOnboardingFilters from "../../components/pi/piOnboardingFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piKpiCard from "../../components/pi/piKpiCard.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piBarList from "../../components/pi/piBarList.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piOnboardingMatrix from "../../components/pi/piOnboardingMatrix.vue";
import piStageBars from "../../components/pi/piStageBars.vue";
import piOnboardingCompany from "../../components/pi/piOnboardingCompany.vue";
import piOnboardingCompanyTable from "../../components/pi/piOnboardingCompanyTable.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piTh from "../../components/pi/piTh.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { columnByKey, screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatDateFull } from "../../components/pi/piFormat.js";
import { ACTIVATION_ORIGIN, companyLink } from "../../components/pi/piNavigation.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, nextSort, optionLabel, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, cancelRequest, isBlocking, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { totalOf } from "../../components/pi/piTable.js";
import { cleanOnboardingFilters, onboardingParams, resolveSalePeriod, stuckIgnoredLabels } from "../../components/pi/piOnboardingFilters.js";
import {
  COHORT_INDICATORS, DEFAULT_SORT, DEFAULTS_NOTE, LIST_PAGE_SIZE, ONBOARDING_BOTTLENECK_COLUMNS, ONBOARDING_MATRIX_COLUMNS, ONBOARDING_STAGES,
  ONBOARDING_STAGE_COLUMNS, ONBOARDING_TIME_COLUMNS, ONBOARDING_TTV_COLUMNS, ONBOARDING_WINDOWS, RULES_NOTE, bottleneckRows, censusNotes,
  cohortColumns, cohortLabelFormatter, cohortRangeText, cohortRows, cohortSeries, cohortTableRows, companyColumns, companyOnboardingView, drillTitle,
  drillToggleText, envelopeWithoutNotes, matrixGroups, matrixTableRows, onboardingCards, onboardingCompanyRows, quantityRows, reasonLabel, sharedNotes,
  stageBarRows, stageTableRows, stuckCounters, summaryHiddenNotes, timeToMilestoneRows, ttvHistogramRows, updatedText, validIndicator, validWindow,
} from "../../components/pi/piOnboardingModel.js";
import {
  PI_COMPANY_ONBOARDING, PI_ONBOARDING_COHORTS, PI_ONBOARDING_COMPANIES, PI_ONBOARDING_STUCK, PI_ONBOARDING_SUMMARY,
} from "../../../../urlConfig";

const FILTER_FIELDS = ["company", "sizeBand", "segment"];
const EXPORT_FIELDS = ["compare", "sizeBand", "segment"];
const CHOICE_STORAGE_KEY = "pi:onboarding-choice";

const keyOf = (url, params) => url + "?" + toQueryString(params);

const sortLine = (columns, state) => {
  const column = state ? columnByKey(columns, state.sort) : null;
  return column ? "Ordem: " + column.label + (state.desc ? ", decrescente" : ", crescente") : "";
};

const readChoice = () => {
  try {
    const raw = window.sessionStorage.getItem(CHOICE_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return { window: validWindow(saved && saved.window), indicator: validIndicator(saved && saved.indicator) };
  } catch (e) {
    return { window: validWindow(null), indicator: validIndicator(null) };
  }
};

const writeChoice = (choice) => {
  try {
    window.sessionStorage.setItem(CHOICE_STORAGE_KEY, JSON.stringify(choice));
  } catch (e) {
    return;
  }
};

export default {
  name: "pi-activation",
  components: {
    boxDefault, filterSelect, piTabs, piFilters, piOnboardingFilters, piDataState, piKpiCard, piLineChart, piBarList, piPageFooter,
    piOnboardingMatrix, piStageBars, piOnboardingCompany, piOnboardingCompanyTable, piDefinitionTooltip, piExportMenu, piExportScreen, piTh,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      windows: ONBOARDING_WINDOWS,
      indicators: COHORT_INDICATORS,
      rulesNote: RULES_NOTE,
      defaultsNote: DEFAULTS_NOTE,
      choice: readChoice(),
      summary: blankRequest(),
      cohorts: blankRequest(),
      stuck: blankRequest(),
      drillCompanies: blankRequest(),
      company: blankRequest(),
      drill: null,
      drillSort: Object.assign({}, DEFAULT_SORT.companies),
      drillPage: 1,
      drillPageSize: LIST_PAGE_SIZE,
      stuckReason: "",
      stuckStage: "",
      stuckSort: Object.assign({}, DEFAULT_SORT.stuck),
      stuckPage: 1,
      stuckPageSize: LIST_PAGE_SIZE,
      stageOptions: ONBOARDING_STAGES.map((stage) => ({ value: String(stage.stage), label: stage.stage + " · " + stage.label })),
    };
  },
  computed: {
    compare() {
      return compareEnabled(this.filters);
    },
    onboarding() {
      return cleanOnboardingFilters(this.filters);
    },
    summaryParams() {
      return onboardingParams(this.filters, "summary");
    },
    summaryKey() {
      return keyOf(PI_ONBOARDING_SUMMARY, this.summaryParams);
    },
    cohortsParams() {
      return onboardingParams(this.filters, "cohorts");
    },
    cohortsKey() {
      return keyOf(PI_ONBOARDING_COHORTS, this.cohortsParams);
    },
    stuckParams() {
      return this.stuckParamsFor(this.stuckPage, this.stuckPageSize);
    },
    stuckKey() {
      return keyOf(PI_ONBOARDING_STUCK, this.stuckParams);
    },
    stuckSource() {
      return { path: PI_ONBOARDING_STUCK, query: this.stuckParams };
    },
    drillParams() {
      return this.drill ? this.drillParamsFor(this.drillPage, this.drillPageSize) : null;
    },
    drillKey() {
      return this.drillParams ? keyOf(PI_ONBOARDING_COMPANIES, this.drillParams) : "";
    },
    drillSource() {
      return { path: PI_ONBOARDING_COMPANIES, query: this.drillParams || {} };
    },
    companyId() {
      return buildApiQuery(this.filters, { fields: ["company"] }).companyId || "";
    },
    companySelected() {
      return !!this.companyId;
    },
    companyKey() {
      return this.companySelected ? PI_COMPANY_ONBOARDING(this.companyId, this.onboarding.shard) : "";
    },
    blocked() {
      return isBlocking(this.summary.error);
    },
    summaryData() {
      return payloadOf(this.summary.envelope);
    },
    summaryEnvelope() {
      return envelopeWithoutNotes(this.summary.envelope, summaryHiddenNotes(this.summary.envelope));
    },
    shownNotes() {
      return sharedNotes(this.summary.envelope);
    },
    cohortsEnvelope() {
      return envelopeWithoutNotes(this.cohorts.envelope, this.shownNotes);
    },
    stuckEnvelope() {
      return envelopeWithoutNotes(this.stuck.envelope, this.shownNotes);
    },
    drillEnvelope() {
      return envelopeWithoutNotes(this.drillCompanies.envelope, this.shownNotes);
    },
    companyEnvelope() {
      return envelopeWithoutNotes(this.company.envelope, this.shownNotes);
    },
    summaryVersion() {
      return this.summary.envelope ? this.summary.envelope.definitionVersion : null;
    },
    headerText() {
      const coverage = this.summaryData.coverage || {};
      const parts = [];
      const updated = updatedText(coverage.updatedAtUtc);
      if (updated) parts.push(updated);
      if (this.summaryVersion) parts.push("Definição v" + this.summaryVersion);
      return parts.join(" · ");
    },
    rangeText() {
      const data = this.summaryData;
      const text = cohortRangeText(data.cohortFrom, data.cohortTo);
      if (text) return text;
      const period = resolveSalePeriod(this.onboarding, new Date());
      return cohortRangeText(period.from, period.to);
    },
    exportPeriodValue() {
      return exportPeriod(this.filters, true);
    },
    exportFilters() {
      return filterLines({ filters: this.filters, fields: EXPORT_FIELDS, onboarding: true });
    },
    indicatorLine() {
      return "Indicador das coortes: " + this.indicatorText;
    },
    cohortExportFilters() {
      return this.exportFilters.concat([this.indicatorLine]);
    },
    drillLines() {
      return this.drill ? ["Lista: " + this.drillTitleText, sortLine(this.drillColumns, this.drillSort)] : [];
    },
    drillExportFilters() {
      return this.exportFilters.concat(this.drillLines);
    },
    stuckLines() {
      const today = formatDateFull(this.stuckData.today);
      const lines = ["Empresas travadas: posição de hoje" + (today !== "—" ? " (" + today + ")" : "")
        + "; a lista não usa o período de vendas, o universo, a situação hoje, o trial nem \"Só as que iniciaram o uso\"."];
      if (this.stuckReason) lines.push("Motivo: " + reasonLabel(this.stuckReason));
      if (this.stuckStage) lines.push("Etapa: " + optionLabel(this.stageOptions, this.stuckStage));
      lines.push(sortLine(this.stuckColumns, this.stuckSort));
      return lines;
    },
    stuckExportFilters() {
      return this.exportFilters.concat(this.stuckLines);
    },
    screenExportFilters() {
      return this.exportFilters.concat([this.indicatorLine], this.drillLines, this.stuckLines);
    },
    cards() {
      return onboardingCards(this.summaryData, this.choice.window);
    },
    matrix() {
      return matrixGroups(this.summaryData, this.compare);
    },
    quantities() {
      return quantityRows(this.summaryData);
    },
    matrixColumns() {
      return ONBOARDING_MATRIX_COLUMNS;
    },
    matrixTable() {
      return matrixTableRows(this.summaryData);
    },
    censusNotes() {
      return censusNotes(this.summaryData.coverage);
    },
    stageBars() {
      return stageBarRows(this.summaryData);
    },
    stageColumns() {
      return ONBOARDING_STAGE_COLUMNS;
    },
    stageTable() {
      return stageTableRows(this.summaryData);
    },
    bottlenecks() {
      return bottleneckRows(this.summaryData, this.compare);
    },
    bottleneckColumns() {
      return ONBOARDING_BOTTLENECK_COLUMNS;
    },
    timeToMilestone() {
      return timeToMilestoneRows(this.summaryData);
    },
    timeColumns() {
      return ONBOARDING_TIME_COLUMNS;
    },
    timeHeaderColumns() {
      return screenColumns(ONBOARDING_TIME_COLUMNS);
    },
    ttvHistogram() {
      return ttvHistogramRows(this.summaryData);
    },
    ttvColumns() {
      return ONBOARDING_TTV_COLUMNS;
    },
    summaryEmpty() {
      return this.summary.loaded && !listOf(this.summaryData.cards).length && !listOf(this.summaryData.milestones).length;
    },
    matrixSelected() {
      return this.drill && this.drill.kind === "milestone" ? { milestone: this.drill.milestone, window: this.drill.window } : null;
    },
    stageSelected() {
      return this.drill && this.drill.kind === "stage" ? { stage: this.drill.stage, window: this.drill.window } : null;
    },
    drillTitleText() {
      return drillTitle(this.drill);
    },
    drillToggle() {
      return drillToggleText(this.drill);
    },
    drillData() {
      return payloadOf(this.drillCompanies.envelope);
    },
    drillRows() {
      return onboardingCompanyRows(this.drillData.rows);
    },
    drillTotal() {
      return totalOf(this.drillData, this.drillRows.length);
    },
    drillColumns() {
      return companyColumns("companies");
    },
    cohortData() {
      return payloadOf(this.cohorts.envelope);
    },
    granularity() {
      return this.onboarding.cohort || "month";
    },
    cohortByText() {
      return this.granularity === "quarter" ? "trimestre" : "mês";
    },
    cohortTitle() {
      return "Coortes por " + this.cohortByText + " da venda";
    },
    indicatorText() {
      const found = COHORT_INDICATORS.filter((item) => item.key === this.choice.indicator)[0];
      return found ? found.label : "";
    },
    cohortRows() {
      return cohortRows(this.cohortData.rows, this.choice.indicator, this.granularity, this.compare);
    },
    cohortRowsRecentFirst() {
      return this.cohortRows.slice().reverse();
    },
    cohortTableColumns() {
      return cohortColumns(this.onboarding.universe);
    },
    cohortHeaderColumns() {
      return screenColumns(this.cohortTableColumns);
    },
    cohortTable() {
      return cohortTableRows(this.cohortData.rows, this.choice.indicator, this.granularity);
    },
    cohortSeries() {
      return cohortSeries(this.cohortData.rows, this.choice.indicator);
    },
    cohortFormatter() {
      return cohortLabelFormatter(this.granularity);
    },
    stuckData() {
      return payloadOf(this.stuck.envelope);
    },
    stuckRows() {
      return onboardingCompanyRows(this.stuckData.rows);
    },
    stuckTotal() {
      return totalOf(this.stuckData, this.stuckRows.length);
    },
    stuckColumns() {
      return companyColumns("stuck");
    },
    counters() {
      return stuckCounters(this.stuckData);
    },
    reasonOptions() {
      const options = this.counters.map((counter) => ({ value: counter.key, label: counter.text }));
      if (this.stuckReason && !options.some((option) => option.value === this.stuckReason)) options.push({ value: this.stuckReason, label: this.stuckReason });
      return options;
    },
    stuckIgnored() {
      const labels = stuckIgnoredLabels(this.filters);
      const today = formatDateFull(this.stuckData.today);
      const base = labels.length ? "Esta lista não usa: " + labels.join(", ") + "; nem o período de vendas" : "";
      return base ? base + (today !== "—" ? " (posição de " + today + ")." : ".") : "";
    },
    companyName() {
      return this.filters.companyName || this.companyId;
    },
    companyFicha() {
      const view = this.companyView;
      return view.company.companyId ? companyLink(view.company.companyId, view.company.shardCode, this.$route.query, ACTIVATION_ORIGIN) : null;
    },
    companyView() {
      return companyOnboardingView(payloadOf(this.company.envelope));
    },
    companyVersion() {
      return this.company.envelope ? this.company.envelope.definitionVersion : null;
    },
    companyEmpty() {
      return this.company.loaded && this.companyView.empty;
    },
  },
  watch: {
    summaryKey() {
      this.drillPage = 1;
      this.loadSummary(false);
    },
    cohortsKey() {
      this.loadCohorts(false);
    },
    stuckKey() {
      this.loadStuck(false);
    },
    drillKey() {
      this.loadDrill(false);
    },
    companyKey() {
      this.loadCompany(false);
    },
  },
  created() {
    this.loadSummary(false);
    this.loadCohorts(false);
    this.loadStuck(false);
    this.loadCompany(false);
  },
  methods: {
    fetch(slot, url, params, key, action, force) {
      const request = this[slot];
      if (!shouldLoad(request, key, force)) return;
      runRequest(request, () => piGet(url, params), action, key);
    },
    loadSummary(force) {
      this.fetch("summary", PI_ONBOARDING_SUMMARY, this.summaryParams, this.summaryKey, "carregar o resumo da ativação", force);
    },
    loadCohorts(force) {
      this.fetch("cohorts", PI_ONBOARDING_COHORTS, this.cohortsParams, this.cohortsKey, "carregar as coortes de ativação", force);
    },
    loadStuck(force) {
      this.fetch("stuck", PI_ONBOARDING_STUCK, this.stuckParams, this.stuckKey, "carregar as empresas travadas", force);
    },
    loadDrill(force) {
      if (!this.drillKey) {
        cancelRequest(this.drillCompanies);
        return;
      }
      this.fetch("drillCompanies", PI_ONBOARDING_COMPANIES, this.drillParams, this.drillKey, "carregar a lista de empresas", force);
    },
    loadCompany(force) {
      if (!this.companyKey) {
        cancelRequest(this.company);
        return;
      }
      this.fetch("company", this.companyKey, {}, this.companyKey, "carregar a ativação da empresa", force);
    },
    stuckParamsFor(page, pageSize) {
      return onboardingParams(this.filters, "stuck", {
        reason: this.stuckReason, stage: this.stuckStage, sort: this.stuckSort.sort, desc: this.stuckSort.desc, page: page, pageSize: pageSize,
      });
    },
    drillParamsFor(page, pageSize) {
      const drill = this.drill || {};
      const extra = { window: drill.window, sort: this.drillSort.sort, desc: this.drillSort.desc, page: page, pageSize: pageSize };
      if (drill.kind === "stage") extra.stage = drill.stage;
      else {
        extra.milestone = drill.milestone;
        extra.reached = drill.reached === true;
      }
      return onboardingParams(this.filters, "companies", extra);
    },
    setChoice(patch) {
      this.choice = Object.assign({}, this.choice, patch);
      writeChoice(this.choice);
    },
    chooseWindow(window) {
      this.setChoice({ window: validWindow(window) });
    },
    chooseIndicator(indicator) {
      this.setChoice({ indicator: validIndicator(indicator) });
    },
    openDrill(drill) {
      this.drill = drill;
      this.drillPage = 1;
      this.$nextTick(() => {
        const title = this.$refs.drillTitle;
        if (title && typeof title.scrollIntoView === "function") title.scrollIntoView({ block: "start", behavior: "smooth" });
        if (title && typeof title.focus === "function") title.focus();
      });
    },
    openMilestone(cell) {
      const same = this.drill && this.drill.kind === "milestone" && this.drill.milestone === cell.milestone && this.drill.window === cell.window;
      if (same) {
        this.closeDrill();
        return;
      }
      this.openDrill({ kind: "milestone", milestone: cell.milestone, window: cell.window, reached: false });
    },
    openStage(segment) {
      const same = this.drill && this.drill.kind === "stage" && this.drill.stage === segment.stage && this.drill.window === segment.window;
      if (same) {
        this.closeDrill();
        return;
      }
      this.openDrill({ kind: "stage", stage: segment.stage, window: segment.window });
    },
    toggleReached() {
      if (!this.drill) return;
      this.drill = Object.assign({}, this.drill, { reached: !this.drill.reached });
      this.drillPage = 1;
    },
    closeDrill() {
      this.drill = null;
      this.drillPage = 1;
    },
    sortDrill(column) {
      this.drillSort = nextSort(this.drillSort, column.sort || column.key, column.startDescending);
      this.drillPage = 1;
    },
    changeDrillPage(page) {
      this.drillPage = Math.max(1, Math.floor(Number(page) || 1));
    },
    changeDrillPageSize(size) {
      this.drillPageSize = size;
      this.drillPage = 1;
    },
    setStuckReason(value) {
      this.stuckReason = value || "";
      this.stuckPage = 1;
    },
    toggleReason(key) {
      this.setStuckReason(this.stuckReason === key ? "" : key);
    },
    setStuckStage(value) {
      this.stuckStage = value || "";
      this.stuckPage = 1;
    },
    sortStuck(column) {
      this.stuckSort = nextSort(this.stuckSort, column.sort || column.key, column.startDescending);
      this.stuckPage = 1;
    },
    changeStuckPage(page) {
      this.stuckPage = Math.max(1, Math.floor(Number(page) || 1));
    },
    changeStuckPageSize(size) {
      this.stuckPageSize = size;
      this.stuckPage = 1;
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-act__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px 16px;
  margin-bottom: 12px;
}

.pi-act__title {
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 700;
  color: #1f2933;
}

.pi-act__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
}

.pi-act__updated {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.pi-act__choice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pi-act__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-act__card-note {
  margin: 0;
  font-size: 12px;
  color: #3a424e;
}

.pi-act__card-hint {
  margin: 2px 0 0;
  font-size: 11px;
  color: #6b7280;
}

.pi-act__note {
  margin-top: 8px;
}

.pi-act__bottlenecks {
  margin: 0 0 14px;
  padding-left: 18px;
  font-size: 13px;
  color: #1f2933;
}

.pi-act__bottlenecks li {
  margin-bottom: 4px;
}

.pi-act__row-name {
  font-weight: 400;
}

.pi-act__open strong {
  color: #6b7280;
  font-weight: 600;
}

.pi-act__counters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 10px;
  padding: 0;
  list-style: none;
}

.pi-act__counters .is-on {
  border-color: #1b59f8;
  background: #eef3ff;
  color: #1340b5;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piActivation.vue